import { useLingui } from '@lingui/react/macro';
import { Minus, Plus } from 'lucide-react';
import { useController, useFormContext, type FieldValues, type Path } from 'react-hook-form';
import { Button } from '@/shared/ui/button';
import { FormField } from '@/shared/ui/field';
import { Input } from '@/shared/ui/input';

/** `StepperTextField` do legado: número com − e +, limites só nos botões (`StepperTextField.tsx:50-81`). */
export function StepperFormField<V extends FieldValues>({
  name,
  label,
  min,
  max,
}: {
  readonly name: Path<V>;
  readonly label: string;
  readonly min: number;
  readonly max: number;
}) {
  const { t } = useLingui();
  const { control } = useFormContext<V>();
  const { field, fieldState } = useController({ name, control });
  const value = typeof field.value === 'number' ? field.value : 0;
  return (
    <FormField name={name} label={label} error={fieldState.error?.message}>
      {(props) => (
        <div className="flex items-center gap-2">
          <Input
            {...props}
            inputMode="numeric"
            value={typeof field.value === 'number' ? String(field.value) : ''}
            onChange={(event) => {
              const text = event.target.value;
              // `onChangeIntallments` (`AutomaticSplitFinancialSettingsDrawerForm.tsx:41-52`): vazio vira 0.
              if (text === '') {
                field.onChange(0);
                return;
              }
              const parsed = Number.parseInt(text, 10);
              if (!Number.isNaN(parsed) && parsed >= 0) field.onChange(parsed);
            }}
            onBlur={field.onBlur}
          />
          <Button
            variant="secondary"
            size="icon"
            aria-label={t`Diminuir ${label}`}
            disabled={value <= min}
            onClick={() => field.onChange(value - 1)}
          >
            <Minus aria-hidden />
          </Button>
          <Button
            variant="secondary"
            size="icon"
            aria-label={t`Aumentar ${label}`}
            disabled={value >= max}
            onClick={() => field.onChange(value + 1)}
          >
            <Plus aria-hidden />
          </Button>
        </div>
      )}
    </FormField>
  );
}
